import { useEffect, useRef } from "react";

// Use the site key from env or fallback to hardcoded value (site keys are public)
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAACpZBY_zpK6olqta";

// Track script loading globally
let scriptLoaded = false;
let scriptLoading = false;

/**
 * Cloudflare Turnstile CAPTCHA component
 * @param {function} onVerify - Callback with token when verified
 * @param {function} onError - Callback when verification fails
 * @param {function} onExpire - Callback when token expires
 * @param {string} className - Additional CSS classes
 */
export default function Turnstile({ onVerify, onError, onExpire, className = "" }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  
  // Store callbacks in refs to prevent re-renders from callback changes
  const callbacksRef = useRef({ onVerify, onError, onExpire });
  
  // Update callback refs without triggering re-render
  useEffect(() => {
    callbacksRef.current = { onVerify, onError, onExpire };
  });

  useEffect(() => {
    let mounted = true;
    
    const renderWidget = () => {
      if (!mounted || !containerRef.current || !window.turnstile) {
        return;
      }
      
      // If already rendered, skip
      if (widgetIdRef.current !== null) {
        return;
      }
      
      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => {
            if (mounted) {
              callbacksRef.current.onVerify?.(token);
            }
          },
          "error-callback": () => {
            if (mounted) {
              callbacksRef.current.onError?.();
            }
          },
          "expired-callback": () => {
            if (mounted) {
              callbacksRef.current.onExpire?.();
            }
          },
          theme: "light"
        });
      } catch (e) {
        console.error("Turnstile render error:", e);
      }
    };

    const loadAndRender = () => {
      // If turnstile is already loaded, render immediately
      if (window.turnstile) {
        scriptLoaded = true;
        renderWidget();
        return;
      }

      // If script is already loading, wait for it
      if (scriptLoading) {
        const checkInterval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(checkInterval);
            scriptLoaded = true;
            renderWidget();
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }

      // Load the script
      scriptLoading = true;
      const script = document.createElement("script");
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.onload = () => {
        scriptLoaded = true;
        scriptLoading = false;
        setTimeout(renderWidget, 100);
      };
      script.onerror = () => {
        scriptLoading = false;
        console.error("Failed to load Turnstile script");
      };
      document.head.appendChild(script);
    };

    loadAndRender();

    // Cleanup on unmount
    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Ignore
        }
        widgetIdRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run once on mount

  if (!TURNSTILE_SITE_KEY) {
    console.warn("Turnstile site key not configured");
    return null;
  }

  return (
    <div 
      ref={containerRef}
      className={`cf-turnstile ${className}`}
      data-testid="turnstile-widget"
    />
  );
}

// Export reset function for external use
export const resetTurnstile = () => {
  if (window.turnstile) {
    window.turnstile.reset();
  }
};
